import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';

import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useAuth } from '../../providers/AuthContext';
import { updateLanguage } from '../../api/profile';
import style from "./Header.module.css";

const Header = () => {
  const { t, i18n: i18nHook } = useTranslation();
  const { isLoggedIn, userName, logout, isCompanyAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = i18nHook.language?.slice(0, 2) || 'uk';

  const handleLangSwitch = async (lang) => {
    if (lang === currentLang) return;
    i18n.changeLanguage(lang);
    if (isLoggedIn) {
      try { await updateLanguage(lang); } catch (e) { /* silent */ }
    }
  };

  // ✅ Стан для перевірки скролу
  const [scrolled, setScrolled] = useState(false);

  // ✅ Перевіряємо чи це головна сторінка
  const isPublicPage = ['/', '/pricing', '/about-us', '/demo', '/download'].includes(location.pathname);

  // ✅ Ефект, який слухає скрол
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // викликаємо одразу, якщо сторінка вже проскролена при завантаженні

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <header
      className={`${style.header} 
                  ${isPublicPage ? style.publicHeader : style.privateHeader} 
                  ${isPublicPage && scrolled ? style.scrolled : ''}`}
    >
      <div className={style.headerContainer}>
        <div className={style.left}>
          {isPublicPage ? (
            <Link to="/">
            <span className={style.logoMaskHome} aria-label="OKboard" />
          </Link>
          ) : (
            <Link to="/">
             <span className={style.logoMask} aria-label="OKboard" />
            </Link>
          )}
        </div>

        <div>
          <nav className={style.nav}>
            <div className={style.langSwitcher}>
              <button
                className={`${style.langBtn} ${currentLang === 'uk' ? style.active : ''}`}
                onClick={() => handleLangSwitch('uk')}
              >
                UK
              </button>
              <span>|</span>
              <button
                className={`${style.langBtn} ${currentLang === 'en' ? style.active : ''}`}
                onClick={() => handleLangSwitch('en')}
              >
                EN
              </button>
            </div>
            {isLoggedIn ? (
              <div className={style.dropdownWrapper}>
                <div className={style.navLink}>
                  <svg className={style.userIconInverse} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.023 15.2923C4.873 14.6616 5.799 14.1635 6.801 13.798C7.80283 13.4327 8.86917 13.25 10 13.25C11.1308 13.25 12.1972 13.4327 13.199 13.798C14.201 14.1635 15.127 14.6616 15.977 15.2923C16.5987 14.6089 17.0912 13.8179 17.4548 12.9192C17.8183 12.0206 18 11.0475 18 10C18 7.78333 17.2208 5.89583 15.6625 4.3375C14.1042 2.77917 12.2167 2 10 2C7.78333 2 5.89583 2.77917 4.3375 4.3375C2.77917 5.89583 2 7.78333 2 10C2 11.0475 2.18175 12.0206 2.54525 12.9192C2.90875 13.8179 3.40133 14.6089 4.023 15.2923ZM10.0003 10.75C9.08725 10.75 8.31733 10.4367 7.6905 9.81C7.0635 9.18317 6.75 8.41325 6.75 7.50025C6.75 6.58725 7.06333 5.81733 7.69 5.1905C8.31683 4.5635 9.08675 4.25 9.99975 4.25C10.9128 4.25 11.6827 4.56333 12.3095 5.19C12.9365 5.81683 13.25 6.58675 13.25 7.49975C13.25 8.41275 12.9367 9.18267 12.31 9.8095C11.6832 10.4365 10.9133 10.75 10.0003 10.75ZM10 19.5C8.68083 19.5 7.44333 19.2519 6.2875 18.7558C5.13167 18.2596 4.12625 17.5839 3.27125 16.7288C2.41608 15.8738 1.74042 14.8683 1.24425 13.7125C0.748083 12.5567 0.5 11.3192 0.5 10C0.5 8.68083 0.748083 7.44333 1.24425 6.2875C1.74042 5.13167 2.41608 4.12625 3.27125 3.27125C4.12625 2.41608 5.13167 1.74042 6.2875 1.24425C7.44333 0.748084 8.68083 0.5 10 0.5C11.3192 0.5 12.5567 0.748084 13.7125 1.24425C14.8683 1.74042 15.8738 2.41608 16.7288 3.27125C17.5839 4.12625 18.2596 5.13167 18.7558 6.2875C19.2519 7.44333 19.5 8.68083 19.5 10C19.5 11.3192 19.2519 12.5567 18.7558 13.7125C18.2596 14.8683 17.5839 15.8738 16.7288 16.7288C15.8738 17.5839 14.8683 18.2596 13.7125 18.7558C12.5567 19.2519 11.3192 19.5 10 19.5Z" fill="var(--inverse-color)" fillOpacity="1"/>
                  </svg>
                  {userName}
                </div>
                <ul className={style.dropdownMenu}>
                  <li>
                    <Link to="/dashboard">
                      <GridViewOutlinedIcon />
                      <span>{t('header.my_boards')}</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/profile">
                      <PersonOutlinedIcon />
                      <span>{t('header.my_profile')}</span>
                    </Link>
                  </li>
                  {isCompanyAdmin && (
                    <li>
                      <Link to="/company">
                        <BusinessOutlinedIcon />
                        <span>{t('header.company')}</span>
                      </Link>
                    </li>
                  )}
                  {isCompanyAdmin && (
                    <li>
                      <Link to="/billing">
                        <CreditCardOutlinedIcon />
                        <span>{t('header.billing')}</span>
                      </Link>
                    </li>
                  )}
                  <li onClick={() => { logout(); navigate('/'); }}
                      className={style.menuListItem}>
                    <LogoutOutlinedIcon />
                    <span>{t('header.logout')}</span>
                  </li>
                </ul>
              </div>
            ) : (
              <Link to="/login" className={`${style.navLink} ${style.highlightedBtn} button event-signup`}>
                <svg className={style.userIcon} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.023 15.2923C4.873 14.6616 5.799 14.1635 6.801 13.798C7.80283 13.4327 8.86917 13.25 10 13.25C11.1308 13.25 12.1972 13.4327 13.199 13.798C14.201 14.1635 15.127 14.6616 15.977 15.2923C16.5987 14.6089 17.0912 13.8179 17.4548 12.9192C17.8183 12.0206 18 11.0475 18 10C18 7.78333 17.2208 5.89583 15.6625 4.3375C14.1042 2.77917 12.2167 2 10 2C7.78333 2 5.89583 2.77917 4.3375 4.3375C2.77917 5.89583 2 7.78333 2 10C2 11.0475 2.18175 12.0206 2.54525 12.9192C2.90875 13.8179 3.40133 14.6089 4.023 15.2923ZM10.0003 10.75C9.08725 10.75 8.31733 10.4367 7.6905 9.81C7.0635 9.18317 6.75 8.41325 6.75 7.50025C6.75 6.58725 7.06333 5.81733 7.69 5.1905C8.31683 4.5635 9.08675 4.25 9.99975 4.25C10.9128 4.25 11.6827 4.56333 12.3095 5.19C12.9365 5.81683 13.25 6.58675 13.25 7.49975C13.25 8.41275 12.9367 9.18267 12.31 9.8095C11.6832 10.4365 10.9133 10.75 10.0003 10.75ZM10 19.5C8.68083 19.5 7.44333 19.2519 6.2875 18.7558C5.13167 18.2596 4.12625 17.5839 3.27125 16.7288C2.41608 15.8738 1.74042 14.8683 1.24425 13.7125C0.748083 12.5567 0.5 11.3192 0.5 10C0.5 8.68083 0.748083 7.44333 1.24425 6.2875C1.74042 5.13167 2.41608 4.12625 3.27125 3.27125C4.12625 2.41608 5.13167 1.74042 6.2875 1.24425C7.44333 0.748084 8.68083 0.5 10 0.5C11.3192 0.5 12.5567 0.748084 13.7125 1.24425C14.8683 1.74042 15.8738 2.41608 16.7288 3.27125C17.5839 4.12625 18.2596 5.13167 18.7558 6.2875C19.2519 7.44333 19.5 8.68083 19.5 10C19.5 11.3192 19.2519 12.5567 18.7558 13.7125C18.2596 14.8683 17.5839 15.8738 16.7288 16.7288C15.8738 17.5839 14.8683 18.2596 13.7125 18.7558C12.5567 19.2519 11.3192 19.5 10 19.5Z" fill="#363D44" fillOpacity="1"/>
                  </svg>
                {t('header.login_btn')}
              </Link>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;